'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Package, Plus, Download, RefreshCw, AlertTriangle } from 'lucide-react';
import InventorySummaryCards from './InventorySummaryCards';
import InventoryFilters from './InventoryFilters';
import InventoryTable from './InventoryTable';
import StockMovementsPanel from './StockMovementsPanel';
import StockAlertPanel from './StockAlertPanel';
import { MOCK_INVENTORY, InventoryItem } from './inventoryData';

export default function InventoryContent() {
  const [items, setItems] = useState<InventoryItem[]>(MOCK_INVENTORY);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStore, setFilterStore] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeView, setActiveView] = useState<'table' | 'movements' | 'alerts'>('table');
  const [refreshing, setRefreshing] = useState(false);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const q = search.toLowerCase();
      const matchSearch = !q || item.itemName.toLowerCase().includes(q) || item.itemCode.toLowerCase().includes(q) || item.category.toLowerCase().includes(q) || item.supplier.toLowerCase().includes(q);
      const matchCat = filterCategory === 'all' || item.category === filterCategory;
      const matchStore = filterStore === 'all' || item.store === filterStore;
      const matchStatus = filterStatus === 'all' || item.stockStatus === filterStatus;
      return matchSearch && matchCat && matchStore && matchStatus;
    });
  }, [items, search, filterCategory, filterStore, filterStatus]);

  const alertItems = items.filter((i) => i.stockStatus === 'critical' || i.stockStatus === 'out-of-stock' || i.stockStatus === 'low');
  const totalValue = items.reduce((s, i) => s + i.totalValue, 0);

  function handleRefresh() {
    setRefreshing(true);
    // Backend: GET /api/inventory (refresh)
    setTimeout(() => { setRefreshing(false); toast.success('Inventory data refreshed'); }, 1000);
  }

  return (
    <div className="px-4 md:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package size={20} style={{ color: 'var(--primary)' }} />
            <h1 className="text-2xl font-bold text-foreground">Inventory & Stores</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {items.length} items · Total value: <strong className="text-foreground font-tabular">UGX {totalValue.toLocaleString()}</strong>
            {alertItems.length > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--danger)' }}>
                <AlertTriangle size={12} />
                {alertItems.length} items need attention
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleRefresh} className="btn-ghost p-2" aria-label="Refresh">
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button className="btn-secondary text-sm py-2 px-3">
            <Download size={14} />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => toast.info('Add inventory item — form coming soon')}
            className="btn-primary text-sm"
          >
            <Plus size={15} />
            Add Item
          </button>
          <button
            onClick={() => toast.info('Record GRN — goods receipt form coming soon')}
            className="btn-secondary text-sm py-2 px-3"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Record GRN</span>
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <InventorySummaryCards items={items} />

      {/* View tabs */}
      <div className="flex gap-1 mb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        {[
          { id: 'view-table', label: 'Stock Items', count: filtered.length },
          { id: 'view-movements', label: 'Stock Movements' },
          { id: 'view-alerts', label: 'Alerts & Expiry', count: alertItems.length, danger: alertItems.length > 0 },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id.replace('view-', '') as typeof activeView)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
            style={{
              borderBottomColor: activeView === tab.id.replace('view-', '') ? 'var(--primary)' : 'transparent',
              color: activeView === tab.id.replace('view-', '') ? 'var(--primary)' : 'var(--muted-foreground)',
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                style={{
                  backgroundColor: tab.danger ? 'var(--danger-bg)' : 'var(--muted)',
                  color: tab.danger ? 'var(--danger)' : 'var(--muted-foreground)',
                  fontSize: '0.6rem',
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeView === 'table' && (
        <>
          <InventoryFilters
            search={search} onSearch={setSearch}
            filterCategory={filterCategory} onCategory={setFilterCategory}
            filterStore={filterStore} onStore={setFilterStore}
            filterStatus={filterStatus} onStatus={setFilterStatus}
          />
          <InventoryTable items={filtered} allItems={items} onItemUpdate={(updated) => setItems((prev) => prev.map((i) => i.id === updated.id ? updated : i))} />
        </>
      )}

      {activeView === 'movements' && <StockMovementsPanel />}
      {activeView === 'alerts' && <StockAlertPanel items={alertItems} />}
    </div>
  );
}